import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { dateToString } from 'modules/core/lib/dates'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import AddShiftContextualButton from 'modules/shift_management/components/ShiftManagement/AddShiftContextualButton'
import DropItem from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CellActions/EmptyCellActions'
import BankHolidayItem from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells//ShiftCell/BankHolidayItem'
import DisabledNightShiftCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/DisabledNightShiftCell'
import EmptyCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/EmptyCell'
import RestDayCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/RestDayCell'
import ShiftCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/ShiftCell'
import LeaveItem from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/ShiftCell/LeaveItem'
import EmptyShiftCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/EmptyShiftCell'
import { getResources } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/getResources'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type { Shift, ShiftsRow } from 'modules/shift_management/lib/types'

interface Props {
  date: moment.Moment
  shiftsRow: ShiftsRow
  isFirstDay: boolean
  isOver: boolean
  contractVersions: ContractVersions
}

const notEndingExactMidnight = (shifts: Shift[], date: moment.Moment) => {
  return shifts.filter((shift) => !shift.isEndNightShiftExactMidnight(date))
}

const EmployeeDayCell = ({ date, shiftsRow, isFirstDay, isOver, contractVersions }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasBackupShifts = hasFeatureFlag(ShiftManagementFeatureFlags.SHIFT_MANAGEMENT_BACKUP_SHIFTS)
  const [{ view }] = useSchedule()

  const { daysByRow, showEmptyShiftCell } = useSelectionState()

  const dayId = dateToString(date, view.dateFormat)
  const isSelected = (daysByRow[shiftsRow.key] || []).includes(dayId)

  const { employee, shifts, leaves, holiday, restDay } = useUnsafeMemo(
    () => getResources(shiftsRow, date, view),
    [shiftsRow, date, view]
  )

  // We need to consider shifts that belong to their calendar day
  // so also regular shifts need to be filtered
  const shiftsToDisplay = useUnsafeMemo(() => notEndingExactMidnight(shifts, date), [shifts, date])
  const nightShiftsToDisplay = useUnsafeMemo(() => {
    const orderedNightShifts = isFirstDay ? shiftsRow.orderedNightShiftsPeriodBefore(date) : []
    return notEndingExactMidnight(orderedNightShifts, date)
  }, [isFirstDay, shiftsRow, date])
  const nightShiftsSet = useUnsafeMemo(
    () => new Set(nightShiftsToDisplay.map((shift: Shift) => shift)),
    [nightShiftsToDisplay]
  )

  const workableLeaves = useUnsafeMemo(() => leaves.filter((leave) => !leave.canHaveBackupShifts))
  const canAddShiftsOnLeaves = workableLeaves.length > 0 || !leaves.length

  const regularShifts = useUnsafeMemo(
    () => (nightShiftsSet?.size ? shifts.filter((shift) => !nightShiftsSet.has(shift)) : shifts),
    [shifts, nightShiftsSet]
  )

  const nightShiftsExcluded = useUnsafeMemo(
    () => shiftsToDisplay.filter((shift) => !nightShiftsSet.has(shift)),
    [shiftsToDisplay, nightShiftsSet]
  )

  if (!employee) return null

  const { canManage } = employee

  const showEmptyCell: boolean =
    (!nightShiftsExcluded.length && !!nightShiftsSet.size) ||
    (!shiftsToDisplay.length && canManage && !restDay && !leaves.length)

  const onlyNightShifts = !!nightShiftsToDisplay?.length && shiftsToDisplay.length == 0

  const weeklyWithBackupShifts = view.isWeekly && hasBackupShifts

  const hasNonBackupShiftsInCell = shiftsRow.nonBackupShifts.some(
    (shift) => shift.date.format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
  )

  return (
    <>
      {!!holiday && <BankHolidayItem holiday={holiday} />}

      {!!leaves.length &&
        !weeklyWithBackupShifts &&
        leaves.map((leave) => (
          <LeaveItem
            key={leave.id}
            leave={leave}
            shiftsRow={shiftsRow}
            date={date}
            contractVersions={contractVersions}
            employee={employee}
          />
        ))}

      {!!restDay && <RestDayCell employee={employee} restDay={restDay} />}

      {!!nightShiftsToDisplay.length && (
        <DisabledNightShiftCell date={date} nightShiftsPeriodBefore={nightShiftsToDisplay} />
      )}

      <ShiftCell
        shifts={regularShifts}
        shiftsRow={shiftsRow}
        employee={employee}
        date={date}
        contractVersions={contractVersions}
      />

      {!!shiftsToDisplay.length && isOver && hasNonBackupShiftsInCell && (
        <DropItem
          shiftsRow={shiftsRow}
          date={date}
          isOver={isOver}
          contractVersions={contractVersions}
        />
      )}

      {/* TODO: we were supposed to remove this when releasing backup shifts for all, but since we
        had to keep the old conflict behaviour for some companies, the FF is still being used.
        Remove this and only keep the upper one if we finally have backup shifts for all.
      */}
      {hasBackupShifts
        ? !showEmptyCell &&
          canManage &&
          !onlyNightShifts &&
          canAddShiftsOnLeaves &&
          hasNonBackupShiftsInCell && (
            <AddShiftContextualButton
              date={date}
              shiftsRow={shiftsRow}
              contractVersions={contractVersions}
            />
          )
        : !showEmptyCell &&
          canManage &&
          !onlyNightShifts && (
            <AddShiftContextualButton
              date={date}
              shiftsRow={shiftsRow}
              contractVersions={contractVersions}
            />
          )}

      {!showEmptyCell && showEmptyShiftCell && isSelected && (
        <EmptyShiftCell employee={employee} contractVersions={contractVersions} />
      )}

      {showEmptyCell && (
        <EmptyCell
          date={date}
          shiftsRow={shiftsRow}
          isOver={isOver}
          contractVersions={contractVersions}
        />
      )}
    </>
  )
}

export default observer(EmployeeDayCell)
